import { isValidTimezone } from "../constants/timezones";

/**
 * Helpers for turning a POST admin/maps/resolve response into form default values.
 *
 * The backend deliberately returns FLAT field names that match the form fields, so
 * most of this is a straight copy. The two things that need real work:
 *
 *  1. timezone — the venue form's select is a fixed list (constants/timezones.js) and
 *     FieldOfCreate silently falls back to the browser's zone when it can't find the
 *     incoming id. Google returns any IANA zone, including ones not on that list
 *     (America/Detroit, America/Boise, America/Indiana/Indianapolis, ...), so an
 *     unmatched zone must be blanked and surfaced rather than quietly replaced.
 *
 *  2. logo — POST admin/maps/logo/import returns a full data URI, but the manual
 *     upload path (FormFields/FileForm.js) stores the base64 payload WITHOUT the
 *     `data:image/png;base64,` prefix into the `cloudAlias` field, and getFillDtoTeam
 *     derives logoUrl from cloudAlias. To produce a byte-identical payload we strip
 *     the prefix and write cloudAlias, not logoUrl.
 */

export const MAPS_TYPES = {
  TEAM: "TEAM",
  VENUE: "VENUE",
};

const GOOGLE_MAPS_LINK_PATTERN =
  /^https?:\/\/([\w-]+\.)*(maps\.app\.goo\.gl|goo\.gl|maps\.google\.[a-z.]+|google\.[a-z.]+\/maps)(\/|\?|$)/i;

/**
 * Mirrors the hosts the backend accepts so the user gets an instant, local error
 * instead of waiting on a round trip for a 400.
 */
export const isGoogleMapsLink = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return false;
  return GOOGLE_MAPS_LINK_PATTERN.test(trimmed);
};

/**
 * FileForm stores `e.target.result.split(",")[1]` — the base64 body only.
 * logo/import returns a full data URI, so strip an identical prefix if present.
 * Already-bare base64 is returned untouched.
 */
export const dataUriToBase64 = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const commaIndex = raw.indexOf(",");
  if (raw.startsWith("data:") && commaIndex !== -1) {
    return raw.slice(commaIndex + 1);
  }
  return raw;
};

const asString = (value) => (value == null ? "" : String(value));

/**
 * ---------------------------------------------------------------------------
 * Abbreviation suggestion
 * ---------------------------------------------------------------------------
 * Google can't supply a team abbreviation, and it's a required field — an empty one was
 * the main reason an autofilled team wouldn't submit. This proposes a starting point that
 * follows how these are written by hand:
 *
 *   - three or more words read best as initials      Suffern High School    -> SHS
 *   - one or two words drop their vowels             Suffern                -> SFFRN
 *   - anything already short enough stands as-is     YMCA / Nitro           -> YMCA / NITRO
 *
 * It only has to be a decent first guess: the field stays editable, and coaches will be
 * able to override it from their profile later.
 */

// Dropped before counting words, so "University of Michigan" is treated as two words.
const ABBREVIATION_NOISE_WORDS = new Set([
  "OF", "THE", "AND", "AT", "FOR", "A", "AN",
  "DE", "LA", "LE", "DU", "DES", "EL",
]);

const normalizeForAbbreviation = (value) =>
  asString(value)
    // Strip accents so an accented letter contributes its base form instead of vanishing.
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toUpperCase()
    // Apostrophes vanish ("Mary's" -> MARYS); other punctuation and hyphens split words.
    .replace(/['‘’]/g, "")
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();

// First character always survives, even when it's a vowel, so the word stays recognisable.
// Digits aren't vowels, so "Team 24" keeps its 24.
const squeezeVowels = (word, limit) => {
  if (!word) return "";
  const rest = word.slice(1).replace(/[AEIOU]/g, "");
  return `${word[0]}${rest}`.slice(0, limit);
};

export const suggestAbbreviation = (name, maxLength = 5) => {
  const normalized = normalizeForAbbreviation(name);
  if (!normalized) return "";

  const allWords = normalized.split(" ").filter(Boolean);
  const significant = allWords.filter(
    (word) => !ABBREVIATION_NOISE_WORDS.has(word),
  );
  // Don't let noise-filtering erase a name that is nothing but noise words.
  const words = significant.length ? significant : allWords;
  if (!words.length) return "";

  // Already an abbreviation, or short enough to use whole: YMCA, NITRO, OHIO.
  if (words.length === 1 && words[0].length <= maxLength) return words[0];

  const initials = words.map((word) => word[0]).join("");

  // Three or more words: initials are what a person would write.
  if (initials.length >= 3) return initials.slice(0, maxLength);

  if (words.length === 1) {
    const squeezed = squeezeVowels(words[0], maxLength);
    // A vowel-heavy word can squeeze down to near nothing; a plain truncation reads better.
    return squeezed.length >= 3 ? squeezed : words[0].slice(0, maxLength);
  }

  // Two words: split the budget so both stay visible rather than spending it all on the first.
  const head = squeezeVowels(words[0], Math.ceil(maxLength / 2));
  const tail = squeezeVowels(words[1], maxLength - head.length);
  const combined = `${head}${tail}`;

  return combined.length >= 3
    ? combined.slice(0, maxLength)
    : normalized.replace(/ /g, "").slice(0, maxLength);
};

/**
 * Returns { timezone, unsupported }.
 *
 * Validity is decided by whether the RUNTIME recognises the zone, not by whether the
 * picker happens to list it — the picker now synthesises an option for any real zone, so
 * an uncurated id like America/Detroit is passed straight through. Only a genuinely
 * unrecognised string is blanked, and that raises a warning so the admin picks one.
 */
export const resolveTimezoneForForm = (timezone) => {
  const incoming = asString(timezone).trim();
  if (!incoming) return { timezone: "", unsupported: false };
  if (isValidTimezone(incoming)) return { timezone: incoming, unsupported: false };
  return { timezone: "", unsupported: true };
};

/**
 * Venue draft -> FieldOfCreate default values.
 *
 * FieldOfCreate reads flat keys (Venues/index.js flattens addressDto the same way
 * for edit), and getFillDtoVenues re-nests them into addressDto on submit.
 */
export const mapVenueDraftToFormValues = (venue) => {
  const draft = venue || {};
  const { timezone, unsupported } = resolveTimezoneForForm(draft.timezone);

  return {
    values: {
      poolName: asString(draft.poolName),
      schoolInstitution: asString(draft.schoolInstitution),
      aquaticCenter: asString(draft.aquaticCenter),
      building: asString(draft.building),
      country: asString(draft.country),
      county: asString(draft.county),
      state: asString(draft.state),
      city: asString(draft.city),
      streetOne: asString(draft.streetOne),
      streetTwo: asString(draft.streetTwo),
      streetThree: asString(draft.streetThree),
      postalCode: asString(draft.postalCode),
      timezone,
      email: asString(draft.email),
      backupEmail: asString(draft.backupEmail),
      googleMapsLink: asString(draft.googleMapsLink),
      mobilePhone: asString(draft.mobilePhone),
      website: asString(draft.website),
      // Collected by the DTO but not by the venue form; pass through so the
      // resolved coordinates are not thrown away.
      gpsCoordinates: asString(draft.gpsCoordinates),
    },
    timezoneUnsupported: unsupported,
  };
};

/**
 * Team draft -> TeamCreateForm default values (flat; Teams/index.js re-nests into
 * contactInformation on submit).
 *
 * Only fields Google can supply are set. Everything else is left to the form's own
 * defaults so we never clobber a select with an empty value it can't render.
 */
export const mapTeamDraftToFormValues = (team) => {
  const draft = team || {};
  const name = asString(draft.name);

  return {
    name,
    // Required by the schema and impossible to derive from a Maps place, so an empty one
    // silently blocked submission. A suggestion here is editable and only has to be close.
    abbreviation: suggestAbbreviation(name),
    country: asString(draft.country),
    county: asString(draft.county),
    state: asString(draft.state),
    city: asString(draft.city),
    streetOne: asString(draft.streetOne),
    streetTwo: asString(draft.streetTwo),
    streetThree: asString(draft.streetThree),
    postalCode: asString(draft.postalCode),
    gpsCoordinates: asString(draft.gpsCoordinates),
    email: asString(draft.email),
    backupEmail: asString(draft.backupEmail),
    googleMapsLink: asString(draft.googleMapsLink),
    mobilePhone: asString(draft.mobilePhone),
    // Deliberately blank: we never populate the work phone from Google.
    workPhone: "",
    website: asString(draft.website),
  };
};

/**
 * The team form's venues field holds full venue OBJECTS, not ids — SelectCheckboxFilter
 * re-normalizes whatever it is given and derives the display `title` itself, so we only
 * have to guarantee a numeric id (options without one are silently dropped).
 *
 * Returns null when the venue has no usable id so callers can skip the link rather than
 * submitting a team with a junk entry in `venues`.
 */
export const toVenueOption = (venue) => {
  const id = Number(venue?.id);
  if (!Number.isFinite(id)) return null;

  return { ...venue, id };
};
